'use client';

import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { Controls } from 'reactflow';

interface PermissionContextType {
    hasAdminPermission: boolean;
    isLoading: boolean;
}

const PermissionContext = createContext<PermissionContextType>({
    hasAdminPermission: false,
    isLoading: true,
});

export function usePermission() {
    return useContext(PermissionContext);
}

interface PermissionProviderProps {
    children: ReactNode;
}

export function PermissionProvider({ children }: PermissionProviderProps) {
    const [userId, setUserId] = useState<string | null>(null);
    const [isClient, setIsClient] = useState(false);

    // Get user ID from localStorage on client-side only
    useEffect(() => {
        setIsClient(true);
        const userStr = localStorage.getItem('user');

        if (userStr) {
            try {
                const user = JSON.parse(userStr);
                const id = user?.id
                if (id) {
                    setUserId(id);
                }
            } catch (error) {
                console.error('Error parsing user data:', error);
            }
        }
    }, []);

    const { data, isLoading } = useQuery({
        queryKey: ['permission', 'check-permission', userId],
        queryFn: async () => {

            const response = await api.post('/api/v1/tapanel/check-permission', {
                user_id: userId,
            });
            return response;
        },
        enabled: isClient && !!userId,
        staleTime: 5 * 60 * 1000,
        retry: false,
    });

    const hasAdminPermission = data?.success === true;

    return (
        <PermissionContext.Provider
            value={{
                hasAdminPermission,
                isLoading: !isClient || (isLoading && !!userId),
            }}
        >   
            {children}
        </PermissionContext.Provider>
    );
}
